/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useLogger } from '@airasia-rn/core-logger';
import { QueryType } from '../components/OriginDestinationSelector/types';

import { StationSearchApiService } from '../services/stationSearchApiService';
import { useAppConfig } from '@airasia-rn/ui';

import {
  apiErrorMeta,
  classifyServiceError,
  errorTrackingEvent,
} from '../analytics';
import { flightKeys } from './flights';
import { getApiPlatform } from '../util/utils';

export function useStationSearchApiService() {
  const appConfig = useAppConfig();
  return useMemo(() => {
    return new StationSearchApiService(appConfig.config.env);
  }, [appConfig.config.env]);
}

export const useStationSearch = (
  queryParams: QueryType | null,
  screenName: string = getApiPlatform(),
) => {
  const apiService = useStationSearchApiService();
  const { logError } = useLogger();

  const enabled = Boolean(
    queryParams && queryParams.query && queryParams.locale,
  );

  const query = useQuery({
    queryKey: [
      ...flightKeys.stationSearch,
      queryParams?.query,
      queryParams?.locale,
      queryParams?.locKey,
      queryParams?.origin,
    ],
    enabled,
    queryFn: ({ signal }) => {
      if (!queryParams) {
        throw new Error('Query params missing');
      }

      return apiService.fetchStationSearch(queryParams, signal);
    },
  });

  // react-query v5 dropped useQuery's onError callback, so error tracking
  // has to observe the query result instead of hooking into the query itself.
  useEffect(() => {
    if (!query.isError) return;

    const { errorType, errorCode, errorDescription, errorMessage } =
      classifyServiceError(query.error);

    logError(
      apiErrorMeta.ERROR_TRACKING_EVENTS.ERROR_TRACKING,
      errorTrackingEvent({
        errorType,
        errorCode,
        errorDescription,
        errorMessage,
        screenName,
        service: apiErrorMeta.SERVICE_NAMES.STATION_SEARCH,
      }),
    );
  }, [query.isError, query.error, screenName, logError]);

  return query;
};
